/**
 * Which locations a set of figures covers, said on screen.
 *
 * Every narrowed figure needs this and for one reason: a single location's
 * line looks exactly like the whole business's. On the CenoHub book, External
 * alone is 1,160 of 1,910 deliveries, and Fire Service alone is 0 of today's
 * 15 — a dashboard reading ₦0 with nothing to say it is showing one location
 * out of four is indistinguishable from a day with no trade, or from data loss.
 * That is not hypothetical: it was reported as a production incident.
 *
 * The growth card has carried this line since the same problem was found
 * there. It lives here now so the card, the dashboard and anything narrowed
 * later cannot drift into saying it differently — or, worse, not saying it.
 */

import type { ReactNode } from 'react';
import { useWorkingLocation } from '../lib/locationContext';
import { describeSelection } from '../lib/geoScope';

export function LocationScope({
  note,
  testId = 'location-scope',
  className = 'text-xs text-slate-600',
}: {
  /** Anything else true of these figures when narrowed, e.g. an excluded total. */
  note?: string;
  testId?: string;
  className?: string;
}): ReactNode {
  const { branchId, location, canSeeAll, canAggregate, selection, setBranchId, setCountry } = useWorkingLocation();
  const may = canSeeAll || canAggregate;

  if (!branchId && !selection.country) {
    return (
      <p className={className} data-testid={testId}>
        Showing <span className="font-semibold text-slate-900">all locations</span>
      </p>
    );
  }

  if (!branchId) {
    // An area: a country, or a state/region within one.
    const area = describeSelection({ ...selection, branchId: null }, null).parts.slice(0, -1).join(' › ');
    return (
      <p className={className} data-testid={testId}>
        Showing <span className="font-semibold text-slate-900">{area}</span> only
        {note ? ` · ${note}` : ''}
        {may ? (
          <>
            {' · '}
            <button
              type="button"
              onClick={() => setCountry(null)}
              className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700"
            >
              Show all locations
            </button>
          </>
        ) : null}
      </p>
    );
  }

  return (
    <p className={className} data-testid={testId}>
      Showing{' '}
      <span className="font-semibold text-slate-900">{location?.name ?? 'one location'}</span> only
      {note ? ` · ${note}` : ''}
      {/*
        The way out, beside the statement. Somebody who has just realised the
        figures are narrowed should not have to go looking for the switcher.
        Offered only to a caller who may widen: for a worker confined to one
        location there is nothing to widen to, and a button that did nothing
        would be worse than none. Widening keeps the country and region: it
        steps up one level, to every location in the same area.
      */}
      {may ? (
        <>
          {' · '}
          <button
            type="button"
            onClick={() => setBranchId(null)}
            className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700"
          >
            Show all locations
          </button>
        </>
      ) : null}
    </p>
  );
}
